import { PropsWithChildren, useEffect, useRef } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import { useAttributesStateStore } from '@/components/datamodel/hooks/useAttributesStore';
import { LayoutProvider } from '@/components/datamodel/hooks/useLayout';
import { useValidationsStore } from '@/components/datamodel/hooks/useValidationsStore';
import { FormResetProvider } from '@/dialogs/item-editor/Context/FormResetContext';
import { logger } from '@/services/logger';
import { PersistedItem } from '@/types/DataModel';

import { useRequiredEditingRow } from './hooks/useEditingRow';

interface EditableRowFormProviderProps {
    /** The row snapshot from React Query used to seed the RHF form. */
    row: PersistedItem;
    /**
     * Layout name whose sections are being edited (defaults to `browse`).
     * The datamodel components (Enum, etc.) read this via `useLayout()`
     * to resolve per-section attribute params.
     */
    layoutName?: string;
}

/**
 * Wraps the currently edited row's cells in a `FormProvider` seeded from
 * the row's React Query snapshot. Every RHF change is forwarded to the
 * three debounced queues via `enqueue`.
 *
 * Mounted from `ItemsList` only for the row whose `primaryKey__` matches
 * `editingRowId` (see `useEditingRow` + the `rowWrapper` prop on `Grid`),
 * so we don't pay the RHF cost on rows the user isn't editing.
 */
const EditableRowFormProvider = ({
    row,
    layoutName = 'browse',
    children,
}: PropsWithChildren<EditableRowFormProviderProps>) => {
    // RHF's `defaultValues` typing rejects unions with `undefined` at the
    // top level, which `PersistedItem` technically has via its optional
    // system fields even though the runtime value is a real row snapshot.
    // Fall back to `PersistedItem`'s `[key: string]: any` index signature.
    const methods = useForm({
        defaultValues: { ...row },
        mode: 'onChange',
    });

    const { enqueue, discardEdit, commitEdit } = useRequiredEditingRow();

    // Keep the latest `discardEdit` / `commitEdit` in refs so the document
    // listeners can stay stable across re-renders. Both callbacks change
    // on every render (their transitive `stopAll` dep is a fresh object
    // from `useItemChangesQueue`), so re-binding the DOM listeners each
    // render would open a small "no listener attached" window where a
    // key press or outside click could be missed.
    const discardEditRef = useRef(discardEdit);
    const commitEditRef = useRef(commitEdit);
    useEffect(() => {
        discardEditRef.current = discardEdit;
    }, [discardEdit]);
    useEffect(() => {
        commitEditRef.current = commitEdit;
    }, [commitEdit]);

    // Anchor to the row's `<tr>`: on mount we look up any cell that
    // rendered with `data-editing-cell` and walk up to the enclosing
    // `<tr>`. Only the currently-editing row has these cells so the
    // query is unambiguous. Stored in a ref because we need it inside
    // long-lived document listeners, not for rendering. The provider
    // only lives as long as the row stays in edit mode, so a single
    // mount-time lookup is enough.
    const rowElementRef = useRef<HTMLElement | null>(null);
    useEffect(() => {
        const cell = document.querySelector('[data-editing-cell="true"]');
        rowElementRef.current = cell?.closest('tr') ?? null;
    }, []);

    // Portal roots that we treat as "logically inside" the editing row
    // even though they render under `document.body`. Without this, opening
    // a Combobox / Select / date picker menu and clicking one of its
    // options would count as a click outside the row and immediately
    // commit + exit edit mode before the option value ever reached the
    // form. Includes the `select-content` class used by our own Combobox
    // (see components/core/Combobox/Combobox.tsx `menu` classNames) plus
    // the Radix popper/portal markers used elsewhere in the app.
    const PORTAL_SELECTOR =
        '.select-content, [data-radix-popper-content-wrapper], [data-radix-portal], [role="listbox"], [role="dialog"], [role="menu"], [role="tooltip"]';

    // Escape (capture) - discard pending changes and exit edit mode.
    // Enter (bubble) - commit pending changes and exit edit mode.
    //
    // The two phases differ intentionally: Escape must run before any
    // child (Combobox / react-select) can call `stopPropagation` on its
    // own Esc handler and swallow it, whereas Enter must let child
    // editors handle it first (a Combobox uses Enter to pick the
    // highlighted option) - if the child stops propagation, we don't
    // reach the bubble handler and we don't commit prematurely.
    useEffect(() => {
        const handleKeyDownCapture = (event: KeyboardEvent) => {
            if (event.key !== 'Escape') return;
            discardEditRef.current();
        };
        const handleKeyDownBubble = (event: KeyboardEvent) => {
            if (event.key !== 'Enter') return;
            // IME composition emits Enter to finish composing; don't treat
            // that as "user finished editing the row".
            if (event.isComposing || event.keyCode === 229) return;
            // Shift/Ctrl/Alt/Meta+Enter often mean "newline" or app-level
            // shortcut - keep them out of the commit path.
            if (event.shiftKey || event.altKey || event.ctrlKey || event.metaKey) {
                return;
            }
            const target = event.target as Node | null;
            const row = rowElementRef.current;
            if (!target || !row) return;
            // Only commit when the Enter came from inside the row (or a
            // portalized editor that belongs to it). This matches how the
            // click-outside handler scopes itself and avoids reacting to
            // Enter presses that originated somewhere else on the page.
            if (!row.contains(target) && !isInsidePortal(target)) return;
            commitEditRef.current();
        };
        document.addEventListener('keydown', handleKeyDownCapture, true);
        document.addEventListener('keydown', handleKeyDownBubble, false);
        return () => {
            document.removeEventListener('keydown', handleKeyDownCapture, true);
            document.removeEventListener('keydown', handleKeyDownBubble, false);
        };
    }, []);

    // Click-outside: any mousedown that lands outside the editing row's
    // `<tr>` (and outside portalized menus/dialogs belonging to a cell
    // editor) commits the current edit and returns the row to read-only.
    // Bound in the capture phase so we see the event before individual
    // cell editors have a chance to `stopPropagation`.
    useEffect(() => {
        const handleMouseDown = (event: MouseEvent) => {
            const target = event.target as Node | null;
            const row = rowElementRef.current;
            if (!target || !row) return;
            if (row.contains(target)) return;
            if (isInsidePortal(target)) return;
            commitEditRef.current();
        };
        document.addEventListener('mousedown', handleMouseDown, true);
        return () =>
            document.removeEventListener('mousedown', handleMouseDown, true);
    }, []);

    // Local helper - captured in the effect closures above. Kept inside
    // the component so the selector list stays next to the comment that
    // explains it.
    function isInsidePortal(target: Node): boolean {
        if (!(target instanceof Element)) return false;
        return Boolean(target.closest(PORTAL_SELECTOR));
    }

    useEffect(() => {
        const subscription = methods.watch((values, { name, type }) => {
            // Filter setValue-driven writes (no `type`) so we don't
            // ping-pong changes coming from remote validation / peer edits.
            if (!type || !name) return;

            const topLevelName = name.includes('.') ? name.split('.')[0] : name;
            const nextValue = (values as Record<string, unknown>)[topLevelName];
            const patch: Partial<PersistedItem> = {
                [topLevelName]: nextValue === undefined ? null : nextValue,
            };

            logger.debug(
                'EditableRowFormProvider: enqueueing patch',
                topLevelName,
                patch,
            );

            enqueue(patch);
        });

        return () => subscription.unsubscribe();
    }, [methods, enqueue]);

    // Clear the shared validation + attribute state stores when this row
    // leaves edit mode (either because the user switched to another row or
    // because `pauseEditing()` was called for a modal). Mirrors the
    // `clearErrors / clearWarnings / clearState` cleanup that the item
    // editor's `useValidations` does on unmount.
    useEffect(() => {
        return () => {
            const validations = useValidationsStore.getState();
            validations.clearErrors();
            validations.clearWarnings();
            validations.clearLocalValidationErrors();
            useAttributesStateStore.getState().clearState();
        };
    }, []);

    // Datamodel components read the current layout via `useLayout()` and the
    // reset-on-save signal via `useFormReset()`. Both providers normally live
    // on the item editor dialog; we mount narrow, row-scoped copies here so
    // Enum / Date / DateTime work identically inside the grid.
    return (
        <LayoutProvider layoutName={layoutName}>
            <FormResetProvider>
                <FormProvider {...methods}>{children}</FormProvider>
            </FormResetProvider>
        </LayoutProvider>
    );
};

export { EditableRowFormProvider };
